import {
  CellValues,
  ColumnHeading,
  PaginatedDataTable,
  TableCellStylesFn,
} from '@lbcamden/lib-react';
import { formatDMY } from '@lbcamden/shared-utils';
import { FunctionComponent } from 'react';
import { useNavigate } from 'react-router';

import { SectionContainer } from '~/client/components/common/SectionContainer';
import { useArrearsArrangementsList } from '~/client/hooks/storeHooks';
import { formatGBP } from '~/client/utils/currency';
import { toArrearsArrangementPage } from '~/client/utils/navigation';
import { ArrearsArrangement } from '~/common/types/arrearsArrangements';

type Props = {
  propertyId: number;
};

type ColumnId = StringKey<ArrearsArrangement>;

const headings = [
  { id: 'startDate', content: 'Start date' },
  { id: 'actionDescription', content: 'Description' },
  { id: 'amount', content: 'Amount' },
] as const satisfies ColumnHeading<ColumnId>[];

const mapNoteToRow = (arrangement: ArrearsArrangement): CellValues<ColumnId> => ({
  ...arrangement,
  _rowId: String(arrangement.arrangementId),
  startDate: formatDMY(arrangement.startDate),
  amount: formatGBP(arrangement.amount),
});

const cellStyles: TableCellStylesFn = (columnId) => [
  { width: columnId === 'actionDescription' ? 200 : 50 },
];

export const ArrearsArrangements: FunctionComponent<Props> = ({ propertyId }) => {
  const navigate = useNavigate();

  const arrangements = useArrearsArrangementsList(propertyId);

  const onClickRow = ({ arrangementId }: ArrearsArrangement) =>
    navigate(toArrearsArrangementPage(propertyId, arrangementId));

  return (
    <SectionContainer title="Arrears arrangements">
      <PaginatedDataTable
        cellStyles={cellStyles}
        headings={headings}
        items={arrangements}
        defaultSort={{ field: 'startDate', direction: 'desc' }}
        mapItemToRow={mapNoteToRow}
        onClickRow={onClickRow}
      />
    </SectionContainer>
  );
};
